import { isV1Url } from './v1-base';

/**
 * v2-routes.enum — the SINGLE SOURCE OF TRUTH for every page route of the PRIMARY
 * application.
 *
 * The redesign is mounted at the application ROOT (`path: ''` — see `app.routes.ts`)
 * and every page is a top-level segment (`/home`, `/care`, `/khoyek`, …). There is no
 * `/v2` prefix any more; the legacy portal lives beneath `/v1` instead.
 * Intra-page navigation is done with `?screen=` query aliases, NOT child routes, so
 * those screen keys are deliberately OUT OF SCOPE here — this file only models the
 * folder-level page routes.
 *
 * Use it EVERYWHERE a route is referenced, so a path is never hand-typed:
 *   · route tables (`path:` / `redirectTo:`) → {@link AppRoute} + {@link ROUTE_SEGMENTS}
 *   · navigation (`Router.navigate`, `navigateByUrl`, `createUrlTree`,
 *     `location.assign`) → {@link APP_PATHS} (ready-made absolute paths) or
 *     {@link buildUrl} (dynamic/compound targets)
 *
 * Mirrors the v1 `*.routes.enum.ts` convention (see `domains/**\/constants`).
 */
export enum AppRoute {
  // ── authenticated pages ─────────────────────────────────────────────────
  HOME = 'home',
  CARE = 'care',
  /**
   * «وصفة طبية عاجلة» — the urgent-prescription request, the destination of the
   * legacy `?isMedicine=true` deep link. A page of its own rather than a mode of
   * `/care`: it is a self-contained feature (`features/medicine`) whose flow,
   * state and chrome are independent of the Care surface, so neither can regress
   * the other. It is NOT a nav tab — it is only ever entered by link.
   */
  MEDICINE = 'medicine',
  KHOYEK = 'khoyek',
  /**
   * «كلمة اليوم» — the daily word game (MVP §2 in-app · §3 the friend's public
   * page `talbinah.net/kalma?r=AB12`). ONE url for both audiences: a signed-in
   * user plays inside the shell, a guest gets the public page. Not a nav tab.
   */
  KALMA = 'kalma',
  COMMUNITY = 'community',
  /**
   * The Community LIBRARY reader, addressed at the root so a shared article /
   * podcast / meditation has a short, human-readable url (`/library/article/98`).
   * It is not a tab of its own — it mounts the Community surface host on COMM7 —
   * but it IS a real route: see `v2-share-routes.ts` for the canonical shapes.
   */
  LIBRARY = 'library',
  PROFILE = 'profile',
  SUBSCRIPTION = 'subscription',
  /**
   * The B2B ORGANIZATION surfaces — `/org/programs` (§6.1), `/org/counseling`
   * (§7, schools only) and `/org/guardian` (§8).
   *
   * NOT a nav tab, and deliberately NOT where the Home Hub lives: «عجلة الأبعاد»
   * and «منظومة الانخراط» stay on `/home`, because a B2B member and an ordinary
   * one use the same screen. These three are separate only because they are full
   * screens with their own states and their own SEO.
   *
   * MEMBER-ONLY: `orgMemberOnlyGuard` (`canMatch`, on the parent route) keeps this
   * whole subtree out of the route table for a non-member, so their `/org/...`
   * falls through to the `**` branded 404 at the url they asked for.
   */
  ORG = 'org',
  /**
   * The account inbox landing. Not a page of its own — the inbox is the global
   * bell PANEL, which is shell-owned and openable from every surface — but it
   * MUST be a real route: it is the path the backend sends a web-push click to
   * when the row has no deep-link template (`NOTIFICATIONS_WEB_DEFAULT_PATH`),
   * and without it that click lands on the `**` branded 404.
   */
  NOTIFICATIONS = 'notifications',

  // ── auth / entry pages (guest-only) ─────────────────────────────────────
  SIGN_IN = 'sign-in',
  /**
   * The pre-migration spelling of {@link SIGN_IN} (the route was `/v2/signin`).
   * Kept ONLY as a redirect alias in `PRIMARY_ROUTES` so an existing bookmark, an SMS
   * link, or a server-authored link that still names it resolves instead of
   * landing on the branded 404. Never navigate to this — use {@link SIGN_IN}.
   */
  SIGN_IN_LEGACY = 'signin',
  ONBOARDING = 'onboarding',
  /** `/invite?code=` — redirect alias to {@link ONBOARDING}; the code is kept for OTP verify (BE11). */
  INVITE = 'invite',

  // ── payment-gateway callback ────────────────────────────────────────────
  PAYMENTS = 'payments',
  STATUS = 'status',

  // ── error surfaces ──────────────────────────────────────────────────────
  ERROR = 'error',
  FORBIDDEN = 'forbidden',
  SERVER = 'server',

  // ── internal tooling ────────────────────────────────────────────────────
  /**
   * The INTERNAL developer surfaces (`/dev/notifications` — the foreground-push
   * simulator). Linked from nowhere, in no nav, in no sitemap, `Disallow`ed in
   * robots.txt and `noindex, nofollow` in their own head: the url is typed by hand.
   *
   * It is a route segment here rather than a string in the route table for the
   * same reason every other path is — so a rename is one edit, and so a reader
   * scanning this file sees the whole url space of the app, tooling included.
   */
  DEV = 'dev',

  /** Catch-all (unmatched → branded 404). */
  WILDCARD = '**',
}

/**
 * The segment the redesign USED to be mounted under, when it was the additive
 * `/v2/...` subtree beside the legacy portal.
 *
 * It is no longer a route — but it is still baked into data we do not control and
 * cannot retro-edit: the backend's `config/notifications.deep_links` templates and
 * `NOTIFICATIONS_WEB_DEFAULT_PATH` author links as `/v2/care/sessions/55`,
 * `/v2/community/posts/91`, `/v2/notifications`, … So every resolver that consumes a
 * SERVER-AUTHORED link normalises it through {@link stripLegacyBase} first and
 * accepts both shapes. Dropping that tolerance would 404 every existing notification
 * until the backend config is redeployed.
 */
export const LEGACY_BASE = 'v2';

/**
 * Route-`data` key marking a PUBLIC surface — a route that deliberately carries NO
 * `authGuard` and must never be redirected away from by the shell's guest-redirect:
 * the payment-gateway callback (it has to show its result first) and the error / 403 /
 * 500 / branded-404 pages (they exist so a failed navigation has somewhere to land).
 *
 * It is route DATA rather than a path test because the branded 404 is served by the
 * `**` wildcard and therefore renders at an arbitrary url that no path check can
 * recognise.
 */
export const PUBLIC_SURFACE_ROUTE_DATA = 'publicSurface';

/**
 * Multi-segment RELATIVE paths for route tables (`path:` values), composed from
 * {@link AppRoute} so the segments stay in one place. Values are plain strings
 * (Angular route `path` never carries a leading slash).
 */
export const ROUTE_SEGMENTS = {
  /** `payments/status` — the Tap card-charge callback landing. */
  PAYMENTS_STATUS: `${AppRoute.PAYMENTS}/${AppRoute.STATUS}`,
  /** `error/forbidden` — access denied (403). */
  ERROR_FORBIDDEN: `${AppRoute.ERROR}/${AppRoute.FORBIDDEN}`,
  /** `error/server` — server / unexpected failure (500). */
  ERROR_SERVER: `${AppRoute.ERROR}/${AppRoute.SERVER}`,
  /** `dev/notifications` — the internal foreground-push simulator. See {@link AppRoute.DEV}. */
  DEV_NOTIFICATIONS: `${AppRoute.DEV}/${AppRoute.NOTIFICATIONS}`,
} as const;

/**
 * Build an ABSOLUTE application URL from segments — for `Router.navigate` command
 * arrays, `navigateByUrl`, `createUrlTree`, and `location.assign`.
 *
 *   buildUrl()                                 → '/'
 *   buildUrl(AppRoute.CARE)                     → '/care'
 *   buildUrl(AppRoute.PAYMENTS, AppRoute.STATUS) → '/payments/status'
 *   buildUrl(tab)                              → '/<tab>'   (dynamic NavKey → tab route)
 */
export function buildUrl(...segments: readonly string[]): string {
  return segments.length ? `/${segments.join('/')}` : '/';
}

/**
 * Ready-made ABSOLUTE paths for every top-level page. Prefer these over `buildUrl(...)`
 * at fixed call-sites so navigation reads declaratively, e.g.
 * `this.router.navigate([APP_PATHS.HOME])`.
 */
export const APP_PATHS = {
  /** `/` — the application root (redirects to HOME). */
  ROOT: buildUrl(),
  HOME: buildUrl(AppRoute.HOME),
  CARE: buildUrl(AppRoute.CARE),
  /** `/medicine` — the urgent-prescription request (legacy `?isMedicine=true`). */
  MEDICINE: buildUrl(AppRoute.MEDICINE),
  KHOYEK: buildUrl(AppRoute.KHOYEK),
  /** `/kalma` — «كلمة اليوم» (in-app when signed in, public friend page otherwise). */
  KALMA: buildUrl(AppRoute.KALMA),
  COMMUNITY: buildUrl(AppRoute.COMMUNITY),
  /** `/library` — the Community library reader surface (COMM6 index · COMM7 by id). */
  LIBRARY: buildUrl(AppRoute.LIBRARY),
  PROFILE: buildUrl(AppRoute.PROFILE),
  SUBSCRIPTION: buildUrl(AppRoute.SUBSCRIPTION),
  /** `/org/programs` — «برامج منشأتك», the programmes the organization paid for. */
  ORG_PROGRAMS: buildUrl(AppRoute.ORG, 'programs'),
  /** `/org/counseling` — «الرعاية والإرشاد». Schools product only. */
  ORG_COUNSELING: buildUrl(AppRoute.ORG, 'counseling'),
  /** `/org/guardian` — the guardian surfaces. Authorised by the granted link, not a role. */
  ORG_GUARDIAN: buildUrl(AppRoute.ORG, 'guardian'),
  /** `/notifications` — the web-push default landing; opens the bell panel on Home. */
  NOTIFICATIONS: buildUrl(AppRoute.NOTIFICATIONS),
  SIGN_IN: buildUrl(AppRoute.SIGN_IN),
  ONBOARDING: buildUrl(AppRoute.ONBOARDING),
  PAYMENTS_STATUS: buildUrl(AppRoute.PAYMENTS, AppRoute.STATUS),
  ERROR: buildUrl(AppRoute.ERROR),
  ERROR_FORBIDDEN: buildUrl(AppRoute.ERROR, AppRoute.FORBIDDEN),
  ERROR_SERVER: buildUrl(AppRoute.ERROR, AppRoute.SERVER),
} as const;

/**
 * Drop a leading legacy `/v2` base from a path, so a SERVER-AUTHORED link written
 * against the old mount resolves against the new root-based routes.
 *
 *   '/v2/care/sessions/55' → '/care/sessions/55'
 *   '/v2'                  → '/'
 *   '/care/sessions/55'    → '/care/sessions/55'   (already root-based — untouched)
 *   '/v2abc'               → '/v2abc'              (not the base segment)
 *
 * Segment-exact, and idempotent, so it is safe to apply to a link of either shape.
 */
export function stripLegacyBase(path: string | null | undefined): string {
  if (!path) return '';
  const trimmed = path.trim();
  const base = `/${LEGACY_BASE}`;
  if (trimmed === base) return '/';
  return trimmed.startsWith(`${base}/`) ? trimmed.slice(base.length) : trimmed;
}

/**
 * Is this url/path a route of the PRIMARY (redesigned) application?
 *
 * Since the redesign owns the root AND the `**` catch-all, this is simply "not
 * legacy" — see {@link isV1Url}, which owns the boundary: the `/v1` subtree, the
 * legacy root compat paths that redirect into it, and the shared root utility pages.
 * An unknown path like `/nonsense` IS a primary-app url: it renders the branded 404.
 *
 * Use it wherever code must decide "does this visit belong to the redesign?" — the
 * shared app root does, to stand down its legacy chrome and `?lang=` / i18n handling.
 */
export function isPrimaryUrl(url: string | null | undefined): boolean {
  if (url === null || url === undefined || url === '') return false;
  return !isV1Url(url);
}
